/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Overview dashboard component for the JB module landing page, displaying stats cards, running jobs with polling, next planned runs table, and recent runs widget
 */
import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject, Subscription, interval} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ConfirmationService} from 'primeng/api';

import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsCrudService} from '../../../../../core/shared/service/crud/mvs-crud.service';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {ObjectRequestListGroupBy} from '../../../../../core/shared/dto/object/request/object-request-list-group-by';
import {ObjectRequestListAttribute} from '../../../../../core/shared/dto/object/request/object-request-list-attribute';
import {ObjectRequestList} from '../../../../../core/shared/dto/object/request/object-request-list';
import {DtoListAttributeRequestAggregateEnum} from '../../../../../core/shared/dto/enum/dto-list-attribute-request-aggregate.enum';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {ObjectIdentifierData} from '../../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {MvsObjectNavigationService} from '../../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from '../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from '../../../../../core/shared/navigation/mvs-object-navigation-action-enum';

import {JbJobRunService} from '../../../service/api/jb-job-run.service';
import {JbSpringBatchService} from '../../../service/api/jb-spring-batch.service';
import {JbSpringBatchStatusDto} from '../../../model/dto/entity/jb-spring-batch-status.dto';
import {JbSpringBatchExecutionDto} from '../../../model/dto/entity/jb-spring-batch-execution.dto';

interface OverviewStat {
    label: string;
    value: number;
    icon: string;
    colorClass: string;
}

@Component({
    selector: 'jb-dashboard-overview',
    templateUrl: 'jb-dashboard-overview.component.html',
    standalone: false
})
export class JbDashboardOverviewComponent implements OnInit, OnDestroy {

    jobTypeService: MvsCrudService;
    busy: boolean;
    initialized: boolean;

    stats: OverviewStat[] = [];
    runningJobs: JbSpringBatchStatusDto;
    nextPlannedRuns: any[] = [];
    recentRunsWidget: WidgetData;

    runtimeCache: Map<number, number> = new Map();

    private destroy$ = new Subject<void>();
    private pollingSubscription: Subscription;

    constructor(
        protected coreService: MvsCoreService,
        protected navigationService: MvsObjectNavigationService,
        protected jobRunService: JbJobRunService,
        protected springBatchService: JbSpringBatchService,
        protected confirmationService: ConfirmationService
    ) {
        this.jobTypeService = this.coreService.getCrudService('jb.JbJobType');
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.refreshComponent();
        this.startPolling();
    }

    /**
     * Refresh Component.
     */
    refreshComponent(): void {
        this.refreshStats();
        this.loadNextPlannedRuns();
        this.refreshRecentRunsWidget();
        this.initialized = true;
    }

    // ---------------------------------------------------------------------------
    // Section A: Stats Bar
    // ---------------------------------------------------------------------------

    /**
     * Refresh stats by querying JbJobType grouped by executionStatus
     * and JbJobRun grouped by jobStatus for the last 24 hours.
     */
    refreshStats(): void {
        const now = new Date();
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        let activeCount = 0;
        let runningCount = 0;
        let errorCount24h = 0;
        let completedCount24h = 0;

        // Query job types grouped by executionStatus (only active ones)
        this.jobTypeService.groupBy(
            ObjectRequestListGroupBy.create(
                false,
                [FilterCriteria.create('entityStatus', FilterCriteria.cOperatorEqual, 3)],
                [],
                ['executionStatus'],
                [new ObjectRequestListAttribute('executionStatus', 'Count', DtoListAttributeRequestAggregateEnum.count)]
            )
        ).subscribe(result => {
            if (result && result.entries) {
                for (const entry of result.entries) {
                    const count = entry['executionStatus_count'] || 0;
                    const status = entry['executionStatus'];
                    activeCount += count;

                    if (status === 1) { // RUNNING (ordinal 1)
                        runningCount = count;
                    }
                }
            }

            this.updateStatsCards(activeCount, runningCount, errorCount24h, completedCount24h);
        });

        // Query job runs grouped by jobStatus for last 24 hours
        this.jobRunService.groupBy(
            ObjectRequestListGroupBy.create(
                false,
                [FilterCriteria.create('startTime', FilterCriteria.cOperatorGreaterEqual, yesterday.toISOString())],
                [],
                ['jobStatus'],
                [new ObjectRequestListAttribute('jobStatus', 'Count', DtoListAttributeRequestAggregateEnum.count)]
            )
        ).subscribe(result => {
            if (result && result.entries) {
                for (const entry of result.entries) {
                    const count = entry['jobStatus_count'] || 0;
                    const status = entry['jobStatus'];

                    // JbJobStatusEnum ordinals: RUNNING=0, COMPLETED=1, ERROR=2, SKIPPED=3
                    if (status === 1) {
                        completedCount24h = count;
                    } else if (status === 2) {
                        errorCount24h = count;
                    }
                }
            }

            this.updateStatsCards(activeCount, runningCount, errorCount24h, completedCount24h);
        });
    }

    /**
     * Build the stats array from collected values.
     */
    private updateStatsCards(activeCount: number, runningCount: number, errorCount: number, completedCount: number): void {
        this.stats = [
            {
                label: 'Aktive Jobs',
                value: activeCount,
                icon: 'fa-regular fa-layer-group',
                colorClass: 'text-primary'
            },
            {
                label: 'Laufend',
                value: runningCount,
                icon: 'fa-regular fa-play',
                colorClass: 'text-cyan-500'
            },
            {
                label: 'Fehler (24h)',
                value: errorCount,
                icon: 'fa-regular fa-circle-xmark',
                colorClass: 'text-red-500'
            },
            {
                label: 'Abgeschlossen (24h)',
                value: completedCount,
                icon: 'fa-regular fa-circle-check',
                colorClass: 'text-green-500'
            }
        ];
    }

    // ---------------------------------------------------------------------------
    // Section B: Running Jobs (Polling)
    // ---------------------------------------------------------------------------

    /**
     * Start polling for running Spring Batch jobs every 10 seconds.
     */
    startPolling(): void {
        this.loadRunningJobs();
        this.pollingSubscription = interval(10000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.loadRunningJobs());
    }

    /**
     * Load currently running Spring Batch jobs.
     */
    loadRunningJobs(): void {
        this.springBatchService.getJobsStatus('%', true).subscribe(status => {
            this.runningJobs = status;
            this.updateRunningRuntimeCache();
        });
    }

    /**
     * Update the runtime cache for all running executions.
     * Cached to avoid change detection issues with recalculating in template.
     */
    updateRunningRuntimeCache(): void {
        if (!this.runningJobs?.executions) return;
        const now = Date.now();
        for (const exec of this.runningJobs.executions) {
            if (exec.startTime) {
                this.runtimeCache.set(exec.executionId, Math.floor((now - new Date(exec.startTime).getTime()) / 1000));
            }
        }
    }

    /**
     * Format runtime seconds into a human-readable string (e.g. "2h 15m 30s").
     */
    formatRuntime(seconds: number): string {
        if (!seconds && seconds !== 0) return '-';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (h > 0) return `${h}h ${m}m ${s}s`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
    }

    /**
     * Confirm and stop a running Spring Batch job execution.
     */
    confirmStopJob(event: Event, execution: JbSpringBatchExecutionDto): void {
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: `Möchten Sie den Job '${execution.jobName}' wirklich stoppen?`,
            header: 'Job stoppen',
            icon: 'fa-regular fa-triangle-exclamation',
            acceptLabel: 'Ja, stoppen',
            rejectLabel: 'Abbrechen',
            accept: () => {
                this.springBatchService.stopJob(execution.executionId).subscribe({
                    next: () => {
                        this.loadRunningJobs();
                    },
                    error: (err) => {
                        console.error('Job konnte nicht gestoppt werden', err);
                    }
                });
            }
        });
    }

    // ---------------------------------------------------------------------------
    // Section C: Next Planned Runs
    // ---------------------------------------------------------------------------

    /**
     * Load the next planned job runs from JbJobType, filtered to active date range + future nextScheduledRunTime.
     */
    loadNextPlannedRuns(): void {
        const now = new Date();
        const today = now.toISOString().split('T')[0]; // YYYY-MM-DD for LocalDate comparison

        const filterCriteria = [
            FilterCriteria.create('entityStatus', FilterCriteria.cOperatorEqual, 3),
            FilterCriteria.create('executionStatus', FilterCriteria.cOperatorNotEqual, 2),
            FilterCriteria.create('startDate', FilterCriteria.cOperatorLowerEqual, today),
            FilterCriteria.create('endDate', FilterCriteria.cOperatorGreaterEqual, today),
            FilterCriteria.create('nextScheduledRunTime', FilterCriteria.cOperatorGreaterThan, now.toISOString()),
        ];

        this.jobTypeService.list(
            ObjectRequestList.createBasic(false, filterCriteria, [new Sorting('nextScheduledRunTime', true)])
        ).subscribe(result => {
            this.nextPlannedRuns = result.entries || [];
        });
    }

    /**
     * Build a German label from recurrenceUnit and recurrenceValue.
     */
    getRecurrenceLabel(recurrenceUnit: number, recurrenceValue: number): string {
        if (recurrenceValue == null || recurrenceUnit == null) return '-';

        const unitLabels: Record<number, string> = {
            0: recurrenceValue === 1 ? 'Minute' : 'Minuten',
            1: recurrenceValue === 1 ? 'Stunde' : 'Stunden',
            2: recurrenceValue === 1 ? 'Tag' : 'Tage',
            3: recurrenceValue === 1 ? 'Woche' : 'Wochen',
        };

        const unitLabel = unitLabels[recurrenceUnit];
        if (!unitLabel) return '-';

        return recurrenceValue === 1 ? `Jede ${unitLabel}` : `Alle ${recurrenceValue} ${unitLabel}`;
    }

    /**
     * Map executionStatus ordinal to a German label and p-tag severity.
     */
    getExecutionStatusInfo(ordinal: number): { label: string, severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' } {
        switch (ordinal) {
            case 0: return {label: 'Bereit', severity: 'success'};
            case 1: return {label: 'Läuft', severity: 'info'};
            case 2: return {label: 'Deaktiviert', severity: 'secondary'};
            default: return {label: '-', severity: 'secondary'};
        }
    }

    /**
     * Confirm and trigger ad-hoc execution of a job type.
     */
    onRunNow(jobType: any): void {
        this.confirmationService.confirm({
            message: `Job '${jobType.name}' jetzt ausführen?`,
            header: 'Job starten',
            icon: 'fa-regular fa-play',
            acceptLabel: 'Ja, starten',
            rejectLabel: 'Abbrechen',
            accept: () => {
                this.jobRunService.runJob(jobType.id).subscribe({
                    next: () => {
                        // Refresh after 3 seconds (backend has 2s delay before async execution)
                        setTimeout(() => this.refreshComponent(), 3000);
                    },
                    error: (err) => {
                        console.error('Job konnte nicht gestartet werden', err);
                    }
                });
            }
        });
    }

    // ---------------------------------------------------------------------------
    // Section D: Recent Runs Widget
    // ---------------------------------------------------------------------------

    /**
     * Refresh the recent runs widget showing today's JbJobRun entries.
     */
    refreshRecentRunsWidget(): void {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: Runtime
         * Reason: JbJobRun table widget with FK resolution for browsing today's job runs sorted by startTime descending
         */
        this.recentRunsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'jb.dashboard.overview.recentRuns',
            'Letzte Läufe',
            'jb.JbJobRun',
            [FilterCriteria.create('e.startTime', FilterCriteria.cOperatorGreaterEqual, today.toISOString())],
            [new Sorting('e.startTime', false)]
        );
    }

    /**
     * Handle row click in the recent runs widget to open JbJobRun in side navigation.
     */
    handleRunSelect(object: ObjectIdentifierData | ObjectIdentifier): void {
        const navigationEntry = MvsObjectNavigationEntry.createNavigationEntry(
            object.objectType,
            object.objectId,
            'object',
            null,
            null,
            null,
            MvsObjectNavigationActionEnum.any,
            'side'
        );
        this.navigationService.navigateTo(navigationEntry, 'right');
    }

    /**
     * Destroy component and clean up all subscriptions.
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
